import React, { useCallback, useEffect, useState } from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import { useQuery } from '@apollo/react-hooks';
import ListingFilter from './listing-filter';
import ListingTable from './listing-table';
import getListingsQuery from '../get-listings.gql';
import styles from './styles-module.css';
import { messages } from './locale';
import {
  SELECT_ALL_LISTINGS,
  SELECT_SOME_LISTINGS,
  LISTINGS_LIMIT,
  ALLOWED_LISTING_STATES,
  SELECTED_LISTINGS_APPLIED,
  ALL_LISTINGS_APPLIED,
  EDIT_LISTINGS,
  CREATE_PAGE,
  RATE_LIMIT,
  POSTED,
  DRAFT
} from '../consts';

const ListingsSelector = injectIntl((props) => {
  const {listingsFilterSetting, setListingsFilterSetting} = props;
  const [listingsMode, setListingsMode] = useState('');
  const [selectedFilterIndex, setSelectedFilterIndex] = useState(0);
  const [searchText, setSearchText] = useState(null);
  const listingSelectorFilters = [
      { text: props.intl.formatMessage(messages.allEventsFilter), value: null },
      { text: props.intl.formatMessage(messages.liveEventsFilter), value: POSTED },
      { text: props.intl.formatMessage(messages.draftEventsFilter), value: DRAFT },
    ];
  const [listings, setListings] = useState([]);
  const { setDataLoader, setEventData } = props

  const { data, loading, fetchMore } = useQuery(getListingsQuery,
    {
      skip: !props.selectedCurrency,
      variables: {
        hostId: props.hostId,
        addOnId: '',
        currency: props.selectedCurrency,
        limit: LISTINGS_LIMIT,
        offset: 0,
        rateLimit: RATE_LIMIT,
      },
      notifyOnNetworkStatusChange: true,
      onCompleted: () =>  setDataLoader(false)
    });

  const showEditButton = props.isEditPage && (SELECTED_LISTINGS_APPLIED === listingsMode);

  useEffect(()=> {
    handleCurrencyChange();
    setDataLoader(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.selectedCurrency]);

  useEffect(()=> {
    if (data && !loading) {
      const { host } = data;
      const list = host?.hosting.nodes.filter(event => ALLOWED_LISTING_STATES.includes(event.state.toUpperCase()));
      setListings(list);
      setEventData(list);
      setListingsMode(selectInitialListingsMode());
    }
  }, [data, loading, selectInitialListingsMode, setEventData, setDataLoader]);

  useEffect(() => {
    if (showEditButton) {
      fetchSelectedEvents();
    }
  },[showEditButton, fetchSelectedEvents])

  const handleCurrencyChange = () => {
    fetchMore({
      variables: {
        currency: props.selectedCurrency,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        return { ...prev, ...fetchMoreResult };
      },
    });
  }

  const selectInitialListingsMode = useCallback(() => {
    const listingIdsCount = props?.listingIds?.length;
    if (!props.isEditPage) {
      return CREATE_PAGE;
    }
    else if (listingsMode === EDIT_LISTINGS) {
      // Once EDIT_LISTINGS listings mode is turned on it is not expected to be turned off
      // EDIT_LISTINGS can be turned on in two ways:
      // 1. Clicking the Edit Events button
      // 2. Clicking one of the event filter buttons
      return listingsMode;
    }
    else if (listingIdsCount >= 1) {
      return SELECTED_LISTINGS_APPLIED;
    }
    else if (listingIdsCount === 0) {
      return ALL_LISTINGS_APPLIED;
    }
    return '';
  }, [props.isEditPage, props.listingIds, listingsMode]);

  const fetchSelectedEvents = useCallback(() => {
    fetchMore({
      variables: {
        limit: LISTINGS_LIMIT,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        return { ...prev, ...fetchMoreResult };
      },
    });
 
    const selectedListingOption = props.listingIds.length >= 1 ? SELECT_SOME_LISTINGS : SELECT_ALL_LISTINGS;
    setListingsMode(EDIT_LISTINGS);
    setListingsFilterSetting(selectedListingOption);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchMore, props.listingIds.length]);

  function selectSubtitle() {
    const { intl, isEditPage } = props;
    if (isEditPage && [EDIT_LISTINGS, ALL_LISTINGS_APPLIED].includes(listingsMode)) {
      return intl.formatMessage(messages.editListingsSubtitle);
    }
    else if (!isEditPage) {
      return intl.formatMessage(messages.eventsSubtitle);
    }
    return '';
  }

  const showFilterButtons = () => {
    if (!props.isEditPage || [EDIT_LISTINGS, ALL_LISTINGS_APPLIED].includes(listingsMode)) {
      return true;
    }
    return false;
  }

  const updateListingsFilterSetting = (setting) => {
    // TODO: Guard if setting = "All" No need to fetch more listings because we don't display any in that case
    fetchMore({
      variables: {
        limit: LISTINGS_LIMIT,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        return { ...prev, ...fetchMoreResult };
      },
    });

    setListingsMode(EDIT_LISTINGS);
    setListingsFilterSetting(setting);
    if (setting === SELECT_SOME_LISTINGS) {
      setSelectedFilterIndex(0);
      setSearchText(null);
    }
  }

  const showTable = () => {
    const { isEditPage } = props;

    if (isEditPage) {
      if (listingsMode === SELECTED_LISTINGS_APPLIED) {
        return true;
      }
      else if (listingsMode === EDIT_LISTINGS) {
        return listingsFilterSetting !== SELECT_ALL_LISTINGS;
      }
    }
    return listingsFilterSetting !== SELECT_ALL_LISTINGS;
  }

  const hideCheckBoxes = () => listingsMode === SELECTED_LISTINGS_APPLIED;

  const renderTitleMessage = () => {
    return <FormattedMessage
      { ...messages.eventsTitle }
      values={{ currencyCode: props.selectedCurrency }}
    />
  }

  const listingsTable = () => {
    const {
      singleCurrency,
      selectedCurrency,
      intl,
      updateListingIds,
      listingIds,
      selectedTicketTypes,
      setSelectedTicketTypes,
      isEditPage,
    } = props;

    if (!selectedCurrency) return null;

    return (
      <div>
        {showFilterButtons() &&
          <ListingFilter
            updateListingsFilterSetting={updateListingsFilterSetting}
            listingsFilterSetting={listingsFilterSetting}
            singleCurrency={singleCurrency}
            selectedCurrency={selectedCurrency}
            updateListingIds={updateListingIds}
            setSelectedTicketTypes={setSelectedTicketTypes}
            intl={intl}
            setDataLoader={setDataLoader}
          />
        }
        {showTable() &&
          <ListingTable
            listings={listings}
            listingIds={listingIds}
            updateListingIds={updateListingIds}
            loading={loading}
            intl={intl}
            hideCheckBoxes={hideCheckBoxes()}
            isEditOrCreateMode={[EDIT_LISTINGS, CREATE_PAGE].includes(listingsMode)}
            hasEvents={listings.length >= 1}
            fetchListings={fetchMore}
            selectedTicketTypes={selectedTicketTypes}
            setSelectedTicketTypes={setSelectedTicketTypes}
            isEditPage={isEditPage}
            listingsMode={listingsMode}
            selectedCurrency={selectedCurrency}
            showEditButton={showEditButton}
            selectedFilterIndex={selectedFilterIndex}
            setSelectedFilterIndex={setSelectedFilterIndex}
            searchText={searchText} 
            setSearchText={setSearchText}
            listingSelectorFilters={listingSelectorFilters}
          />
        }
      </div>
    );
  }

  return (
    <section className={`${styles.form} ${styles.listingSelectorForm}`}>
      <div>
        <h3 className={`${styles.basicInfoHeader} ${styles.eventsSelectorTitle}`}>
          {renderTitleMessage()}
        </h3>
      </div>
      <p className={styles.subtitle}>
        {selectSubtitle()}
      </p>
      {listingsTable()}
    </section>
  );
})

export default ListingsSelector;
